<template>
  <WithLoader id="answer-usages" class="flex flex-col" :loading="isLoading" use-as-overlay>
    <div class="flex pb-5 border-solid border-b border-0 border-soft">
      <span class="my-auto py-1 ml-6">
      <base-button variant="transparent-primary"
                   icon="keyboard_arrow_left"
                   @click="hideAnswerUsages"></base-button>
      </span>

      <span class="my-auto px-4 ml-4 border-solid border-l-2 border-0 border-soft text-gray-dark" v-html="summaryText"></span>
    </div>

    <div class="flex flex-col gap-5 mt-10">
      <div v-if="usedInAnswer">
        <div class="flex flex-row items-center text-gray-dark ml-6">
          <span class="text-lg font-bold ml-px">Answers</span>
          <AnswersIcon class="ml-2 h-5" />
        </div>

        <UsagesList :list="answersUsageList" />
      </div>

      <div v-if="usedInEvent">
        <div class="flex flex-row items-center text-gray-dark ml-6">
          <span class="text-lg font-bold ml-px">Events</span>
          <EventsIcon class="ml-2 h-5" />
        </div>

        <UsagesList :list="eventsUsageList" />
      </div>
    
      <div v-if="usedInTemplate">
        <div class="flex flex-row items-center text-gray-dark ml-6">
          <span class="text-lg font-bold ml-px">Blocks</span>
          <BlocksIcon class="ml-2 h-5 mb-0.5" />
        </div>

        <UsagesList :list="templatesUsageList" />
      </div>

    </div>
  </WithLoader>
</template>

<script>
import answerModule from '@/store/modules/answer.module'
import answerService from '@/services/amio/answer.service'
import UsagesList from './UsagesList'
import WithLoader from '@/components/common/WithLoader'
import AnswersIcon from '@/assets/images/icons/AnswersIcon'
import EventsIcon from '@/assets/images/icons/EventsIcon'
import BlocksIcon from '@/assets/images/icons/BlocksIcon'
import isEmpty from 'ramda/src/isEmpty'
import { ROUTE_NAMES } from '@/router'

export default {
  name: 'AnswerUsages',
  components: { UsagesList, WithLoader, AnswersIcon, EventsIcon, BlocksIcon },

  props: {
    botId: {
      type: String,
      required: true
    },
    answerId: {
      type: String,
      required: true
    }
  },

  data() {
    return {
      isLoading: false,
      answers: [],
      events: [],
      templates: []
    }
  },

  computed: {
    usedInAnswer() {
      return !isEmpty(this.answersUsageList)
    },

    usedInEvent() {
      return !isEmpty(this.eventsUsageList)
    },

    usedInTemplate() {
      return !isEmpty(this.templatesUsageList)
    },

    summaryText() {
      if (!this.usedInAnswer && !this.usedInEvent && !this.usedInTemplate) {
        return 'The answer is currently unused'
      }

      const summaryStrings = ['The answer is used in']
      if (this.usedInAnswer) {
        const answerCount = this.answersUsageList.length
        summaryStrings.push(`<strong>${answerCount} ${answerCount > 1 ? 'Answers' : 'Answer'}</strong>`)
      }

      if (this.usedInEvent) {
        if (this.usedInAnswer) {
          summaryStrings.push('and')
        }
        const eventCount = this.eventsUsageList.length
        summaryStrings.push(`<strong>${eventCount} ${eventCount > 1 ? 'Events' : 'Event'}</strong>`)
      }

      if (this.usedInTemplate) {
        if (this.usedInAnswer || this.usedInEvent) {
          summaryStrings.push('and')
        }
        const templateCount = this.templatesUsageList.length
        summaryStrings.push(`<strong>${templateCount} ${templateCount > 1 ? 'Blocks' : 'Block'}</strong>`)
      }

      return summaryStrings.join(' ')
    },

    answersUsageList() {
      return this.answers.map(answer => {
        const answerUsage = {
          title: answer.name,
          subtitle: ''
        }
        
        if (answer.id === this.answerId) {
          answerUsage.text = 'Current Answer'
        } else {
          answerUsage.routeDetails = {
            name: ROUTE_NAMES.answers,
            params: {
              botId: this.botId,
              answerId: answer.id
            }
          }
        }

        return answerUsage
      })
    },

    eventsUsageList() {
      return this.events.map(event => {
        return {
          title: event.name,
          subtitle: event.description,
          routeDetails: {
            name: ROUTE_NAMES.events,
            params: {
              botId: this.botId,
              eventId: event.id
            }
          }
        }
      })
    },

    templatesUsageList() {
      return this.templates.map(template => {
        return {
          title: template.name,
          subtitle: '',
          routeDetails: {
            name: ROUTE_NAMES.blocks,
            params: {
              botId: this.botId,
              blockId: template.id
            }
          }
        }
      })
    }
  },

  methods: {
    hideAnswerUsages() {
      answerModule.hideUsages()
    }
  },

  async mounted() {
    this.isLoading = true
    const { answers, events, templates } = await answerService.getReferences(this.botId, this.answerId)
    this.answers = answers
    this.events = events
    this.templates = templates
    this.isLoading = false
  },

  destroyed() {
    this.hideAnswerUsages()
  }
}
</script>
